import { isNil } from 'es-toolkit';
import { isEmpty } from 'es-toolkit/compat';

import { appRoutes, RouterEntitiesId } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import { PeoPayrollProviderInfoFragment, PeoPayrollProviderWhere, UserStatus } from 'src/api';

import { buildUrl } from './router';

type PeoPayrollProvidersTableFilter = {
  status?: Nullable<UserStatus[]>;
};

const determinePeoPayrollProviderStatus = (
  peoPayrollProviderStaffs: PeoPayrollProviderInfoFragment['peoPayrollProviderStaffsList'],
): UserStatus => {
  if (isEmpty(peoPayrollProviderStaffs)) {
    return UserStatus.Inactive;
  }

  if (
    peoPayrollProviderStaffs.some(
      peoPayrollProviderStaff => peoPayrollProviderStaff.user.status === UserStatus.Active,
    )
  ) {
    return UserStatus.Active;
  }

  if (
    peoPayrollProviderStaffs.every(
      peoPayrollProviderStaff => peoPayrollProviderStaff.user.status === UserStatus.Pending,
    )
  ) {
    return UserStatus.Pending;
  }

  return UserStatus.Inactive;
};

export const getPeoPayrollProviderStatus = (
  peoPayrollProvider: PeoPayrollProviderInfoFragment,
): UserStatus => {
  return determinePeoPayrollProviderStatus(peoPayrollProvider.peoPayrollProviderStaffsList);
};

export const preparePeoPayrollProvidersFilterByStatus = (
  filter: PeoPayrollProvidersTableFilter,
): PeoPayrollProviderWhere | undefined => {
  if (isNil(filter.status) || isEmpty(filter.status)) return undefined;

  const hasActiveUser = {
    peoPayrollProviderStaffs: {
      some: {
        user: { status: { equals: UserStatus.Active } },
      },
    },
  } satisfies PeoPayrollProviderWhere;

  const hasPendingUser = {
    peoPayrollProviderStaffs: {
      some: {
        user: { status: { equals: UserStatus.Pending } },
      },
    },
  } satisfies PeoPayrollProviderWhere;

  const hasInactiveUser = {
    peoPayrollProviderStaffs: {
      some: {
        user: { status: { equals: UserStatus.Inactive } },
      },
    },
  } satisfies PeoPayrollProviderWhere;

  const hasAnyUser = {
    peoPayrollProviderStaffs: {
      some: {},
    },
  } satisfies PeoPayrollProviderWhere;

  return {
    OR: filter.status.map(status => {
      // Active: at least one User with Active status
      if (status === UserStatus.Active) {
        return hasActiveUser;
      }

      // Pending: no Active users, all users are Pending (at least one exists)
      if (status === UserStatus.Pending) {
        return {
          AND: [{ NOT: [hasActiveUser] }, hasPendingUser, { NOT: [hasInactiveUser] }],
        };
      }

      // Inactive: no Active users AND (no users at all OR at least one Inactive user)
      return {
        AND: [
          { NOT: [hasActiveUser] },
          {
            OR: [{ NOT: [hasAnyUser] }, hasInactiveUser],
          },
        ],
      };
    }),
  };
};

export const buildPeoPayrollProviderDetailsUrl = (peoPayrollProviderId: string) => {
  return buildUrl(appRoutes.peoPayrollProviders.detail.companyInformation, {
    [RouterEntitiesId.peoPayrollProvider]: peoPayrollProviderId,
  });
};
